import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  Divider,
  Tooltip,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { green } from '@mui/material/colors';
import { ChannelIconBadge } from '../../../components/chat/ChannelIconBadge';
import { TicketPriorityBadge } from '../../../components/ticket/TicketPriorityBadge';
import { TicketStatusBadge } from '../../../components/ticket/TicketStatusBadge';
import type { TicketListItemProps } from '../types/tickets.types';
import { TicketStatus } from '../../../types/chat.types';
import { TicketTabFilter } from '../types/tickets.types';
import { formatMessagePreview } from '../../../utils/messagePreview';
import { useContactAvatarUrl } from '../../../hooks/useContactAvatarUrl';
import { useAppLocale } from '../../../hooks/useAppLocale';
import { getRelativeTime } from '../../../utils/dateFormat';
import { formatPhoneForDisplay } from '../../../utils/phone';

/**
 * TicketListItem - Individual ticket item with enriched card layout
 *
 * Features:
 * - Queue color dot indicator
 * - Contact avatar with channel badge
 * - Priority flag icon
 * - Status badge for non-open tickets
 * - Relative timestamps ("5m", "2h", "Yesterday")
 * - Last message preview with unread count
 * - Right-click context menu support
 * - Accept button for pending tickets
 */
export function TicketListItem({
  ticket,
  isSelected = false,
  onAccept,
  onClick,
  onContextMenu,
  onSpy,
  groupActionButtons = false,
  activeTab,
}: TicketListItemProps) {
  const navigate = useNavigate();
  const translate = useTranslate();
  const appLocale = useAppLocale();
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleAcceptTicket = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onAccept) return;

    setLoading(true);
    try {
      await onAccept(ticket);
    } catch (err) {
      console.error('Failed to accept ticket:', err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleClick = () => {
    if (ticket.status === TicketStatus.PENDING && (groupActionButtons || !ticket.conversation?.isGroup)) {
      return;
    }
    if (onClick) {
      onClick(ticket);
    } else {
      navigate(`/tickets?ticket=${ticket.id}`);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onContextMenu?.(e, ticket);
  };

  // Get contact info from ticket
  const contact = ticket.contact || ticket.conversation?.contact;
  const contactName = contact?.name || translate('resources.tickets.unknownContact');
  const resolvedAvatarUrl = useContactAvatarUrl(contact?.avatarFileId);
  const avatarUrl = contact?.profilePicUrl || resolvedAvatarUrl;

  // Get queue color (fallback to gray)
  const queueColor = ticket.queue?.color || ticket.ticketQueue?.color || '#7C7C7C';
  const queueName = ticket.queue?.name || ticket.ticketQueue?.name || translate('resources.tickets.noQueue');
  const hasQueueColor = queueColor !== '#7C7C7C';

  // Get channel info from ticket (flattened from backend)
  const channelType = ticket.channelType;
  const channelConnectionName = ticket.channelConnectionName;

  // Get assigned user
  const assignedUserName = ticket.assignedUser?.name || ticket.assignedUser?.email;

  // Relative timestamp
  const relativeTime = getRelativeTime(ticket.updatedAt, translate, appLocale);

  const isPending = ticket.status === TicketStatus.PENDING;
  const isOpen = ticket.status === TicketStatus.OPEN;

  // Build a conversation-like object from flat ticket fields for message preview
  const conversationPreviewData = ticket.conversation ?? (ticket.lastMessage || ticket.lastMessageType
    ? {
        lastMessage: ticket.lastMessage ?? null,
        lastMessageType: (ticket.lastMessageType as any) ?? null,
        channelConnection: null,
      } as any
    : null);

  // Get smart last message preview (channel-aware, with media type indicators)
  const lastMessagePreview = conversationPreviewData
    ? formatMessagePreview(conversationPreviewData, { maxLength: 60, translate })
    : '';

  // Longer preview for tooltip on pending tickets
  const fullMessagePreview = isPending && conversationPreviewData
    ? formatMessagePreview(conversationPreviewData, { maxLength: 300, translate })
    : '';

  // Unread messages count
  const unreadCount = ticket.conversation?.unreadCount || 0;

  // Build contact tooltip with name, phone, and email
  const hasContactDetails = Boolean(contactName || contact?.phoneNumber || contact?.email);
  const phoneFormatted = contact?.phoneNumber ? formatPhoneForDisplay(contact.phoneNumber) : null;

  // Hide status badge entirely when the context already makes it obvious:
  // - Pending tickets in the "Meus" tab (they're in the dedicated pending section)
  // - Any ticket in the "Sem atribuição" tab (defaults to pending only)
  const isStatusRedundant = isPending && (
    activeTab === TicketTabFilter.MINE || activeTab === TicketTabFilter.UNASSIGNED
  );

  return (
    <>
      <ListItem
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        sx={{
          position: 'relative',
          cursor: isPending && (groupActionButtons || !ticket.conversation?.isGroup) ? 'default' : 'pointer',
          py: 1,
          px: 2,
          bgcolor: isSelected ? 'action.selected' : undefined,
          '&:hover': {
            bgcolor: isSelected ? 'action.selected' : 'action.hover',
          },
        }}
      >
        <ListItemAvatar>
          <Box sx={{ position: 'relative', mr: channelType ? 1 : 0 }}>
            <Avatar
              src={avatarUrl ?? undefined}
              sx={{ width: 40, height: 40 }}
            >
              {!avatarUrl && contactName[0]?.toUpperCase()}
            </Avatar>
            <ChannelIconBadge
              channelType={channelType}
              connectionName={channelConnectionName}
              channelTypeName={ticket.channelTypeName}
              mode="badge"
              size={16}
            />
          </Box>
        </ListItemAvatar>

        <ListItemText
          disableTypography
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Queue color dot */}
              {hasQueueColor && (
                <Tooltip title={queueName} arrow placement="top">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: queueColor,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}

              {/* Contact name */}
              <Tooltip
                title={hasContactDetails ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Contact name with icon */}
                    {contactName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}>
                        <PersonIcon sx={{ fontSize: 14 }} />
                        <span>{contactName}</span>
                      </Box>
                    )}
                    {/* Phone number */}
                    {phoneFormatted && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PhoneIcon sx={{ fontSize: 14 }} />
                        <Box>
                          <Box component="span">{phoneFormatted.national}</Box>
                          {phoneFormatted.international !== phoneFormatted.national && (
                            <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontSize: '0.8em' }}>
                              ({phoneFormatted.international})
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                    {/* Email */}
                    {contact?.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <EmailIcon sx={{ fontSize: 14 }} />
                        <span>{contact.email}</span>
                      </Box>
                    )}
                  </Box>
                ) : ''}
                placement="top-start"
                enterDelay={400}
                arrow
                slotProps={{
                  tooltip: { sx: { maxWidth: 300, fontSize: 13 } },
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: unreadCount > 0 ? 700 : 400,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {contactName}
                </Typography>
              </Tooltip>

              {/* Status icon for non-open tickets (hidden when context makes it obvious) */}
              {!isOpen && ticket.status && !isStatusRedundant && (
                <TicketStatusBadge
                  status={ticket.status}
                  size="small"
                  iconOnly
                />
              )}

              {/* Priority flag */}
              {ticket.priority && (
                <TicketPriorityBadge priority={ticket.priority} iconOnly size="small" />
              )}

              {/* Relative timestamp */}
              {relativeTime && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ flexShrink: 0, ml: 0.5, fontSize: '0.75rem' }}
                >
                  {relativeTime}
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {(lastMessagePreview || unreadCount > 0) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tooltip
                    title={isPending && fullMessagePreview ? fullMessagePreview : ''}
                    placement="bottom-start"
                    enterDelay={400}
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 320,
                          fontSize: 13,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      noWrap
                      sx={{
                        fontWeight: unreadCount > 0 ? 600 : 400,
                        pr: 1,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {lastMessagePreview}
                    </Typography>
                  </Tooltip>

                  {unreadCount > 0 && (
                    <Badge
                      badgeContent={unreadCount}
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'relative',
                          transform: 'none',
                          bgcolor: green[500],
                          color: 'white',
                          fontWeight: 600,
                        },
                        mr: 1,
                      }}
                    />
                  )}
                </Box>
              )}

              {(assignedUserName || channelConnectionName) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {assignedUserName && (
                    <>
                      <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        noWrap
                        sx={{ fontSize: 11 }}
                      >
                        {assignedUserName}
                      </Typography>
                    </>
                  )}
                  {assignedUserName && channelConnectionName && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: 11 }}>
                      ·
                    </Typography>
                  )}
                  {channelConnectionName && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      noWrap
                      sx={{ fontSize: 11 }}
                    >
                      {channelConnectionName}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          }
        />

        {/* Ticket actions */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0, alignSelf: 'center' }}>
          {/* Spy button - available for all tickets */}
          {onSpy && (
            <Tooltip title={translate('resources.tickets.buttons.spy')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onSpy(ticket);
                }}
                sx={{ color: 'info.main' }}
              >
                <RemoveRedEyeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Accept button for pending tickets */}
          {isPending && (groupActionButtons || !ticket.conversation?.isGroup) && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAcceptTicket}
              disabled={loading}
              sx={{ minWidth: 72 }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                translate('resources.tickets.buttons.accept')
              )}
            </Button>
          )}
        </Box>
      </ListItem>
      <Divider variant="inset" component="li" />
    </>
  );
}

export default TicketListItem;
